import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";

function TimelineEvent({ ev }) {
  const KIND_LABEL = {
    report_requested:      "Report requested",
    report_sent:           "Free report sent",
    report_failed:         "Report failed to generate",
    trial_started:         "Trial started",
    subscription_started:  "Subscription active",
    churned:               "Subscription ended",
    note_added:            "Internal note added",
    archived:              "Lead archived",
    unarchived:            "Lead restored",
  };
  const dt = ev.ts ? new Date(ev.ts) : null;
  return (
    <div className="flex gap-3 py-2.5">
      <div className="w-2 h-2 rounded-full bg-charcoal-800 mt-2 shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-medium text-charcoal-900">{KIND_LABEL[ev.kind] || ev.kind}</p>
        {ev.note && <p className="text-xs text-charcoal-500 mt-0.5">{ev.note}</p>}
        <p className="text-[10.5px] font-mono uppercase tracking-widest text-charcoal-400 mt-0.5">
          {dt ? dt.toLocaleString() : ""}
        </p>
      </div>
    </div>
  );
}

const STAGE_STYLE = {
  new:      "bg-charcoal-50 text-charcoal-700 border-black/10",
  trial:    "bg-blue-50 text-blue-700 border-blue-200",
  customer: "bg-emerald-100 text-emerald-800 border-emerald-200",
  churned:  "bg-charcoal-100 text-charcoal-600 border-black/10",
  archived: "bg-charcoal-100 text-charcoal-500 border-black/10",
};
const STAGE_LABEL = {
  new:      "New lead",
  trial:    "Trial user",
  customer: "Paying customer",
  churned:  "Former customer",
  archived: "Archived",
};

export default function AdminLeadDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [archiving, setArchiving] = useState(false);

  async function load() {
    const r = await api.get(`/admin/leads/${id}`);
    setData(r.data);
  }
  useEffect(() => { load().catch(() => setData({ error: true })); }, [id]);

  async function addNote(e) {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/leads/${id}/notes`, { text: noteText });
      setNoteText("");
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(data.lead.email || "");
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1500);
    } catch {
      // clipboard blocked — fall through silently, mailto still works
    }
  }

  async function toggleArchive() {
    const archived = !!data?.lead?.archived_at;
    if (!archived) {
      const reason = window.prompt("Optional: reason for archiving (spam, duplicate, not-a-fit)?");
      if (reason === null) return;   // user hit cancel
      setArchiving(true);
      try {
        await api.post(`/admin/leads/${id}/archive`,
          { reason: reason || null });
        await load();
      } finally { setArchiving(false); }
    } else {
      setArchiving(true);
      try {
        await api.post(`/admin/leads/${id}/unarchive`, {});
        await load();
      } finally { setArchiving(false); }
    }
  }

  if (!data) return <div className="p-8 text-sm text-charcoal-400">Loading…</div>;
  if (data.error) return <div className="p-8 text-sm text-red-600">Lead not found.</div>;

  const lead = data.lead;
  const timeline = [...(lead.timeline || [])].reverse();  // most recent first
  const stage = data.stage || "new";
  const timesSeen = data.times_seen || 1;
  const archived = !!lead.archived_at;

  return (
    <div className="min-h-screen bg-parchment">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Link to="/admin/leads" data-testid="lead-back" className="text-xs font-mono uppercase tracking-widest text-charcoal-500 hover:text-charcoal-900">← Back to Leads</Link>
        <div className="mt-3 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Lead detail</p>
              <span data-testid="lead-stage-chip"
                    className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${STAGE_STYLE[stage]}`}>
                {STAGE_LABEL[stage]}
              </span>
              {data.needs_followup && !archived && (
                <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border bg-amber-50 text-amber-700 border-amber-200">
                  Needs follow-up
                </span>
              )}
            </div>
            <h1 className="mt-1 text-3xl font-semibold tracking-tighter text-charcoal-900" data-testid="lead-detail-org">
              {lead.organization_name}
            </h1>
            <p className="text-sm text-charcoal-500 mt-1">
              <a href={lead.website} target="_blank" rel="noreferrer" className="hover:underline" data-testid="lead-website-link">{lead.website}</a>
              {" · "}
              <a href={`mailto:${lead.email}`} className="hover:underline" data-testid="lead-mailto">{lead.email}</a>
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={copyEmail}
                      data-testid="lead-copy-email"
                      className="px-2.5 py-1 rounded-full border border-black/10 bg-white text-[11px] font-mono uppercase tracking-widest text-charcoal-700 hover:border-black/20">
                {emailCopied ? "Copied ✓" : "Copy email"}
              </button>
              <a href={`mailto:${lead.email}`}
                 data-testid="lead-email-btn"
                 className="px-2.5 py-1 rounded-full border border-black/10 bg-white text-[11px] font-mono uppercase tracking-widest text-charcoal-700 hover:border-black/20">
                Send email
              </a>
              <a href={lead.website} target="_blank" rel="noreferrer"
                 data-testid="lead-open-site"
                 className="px-2.5 py-1 rounded-full border border-black/10 bg-white text-[11px] font-mono uppercase tracking-widest text-charcoal-700 hover:border-black/20">
                Open website ↗
              </a>
              <button onClick={toggleArchive}
                      disabled={archiving}
                      data-testid="lead-archive-btn"
                      className={`px-2.5 py-1 rounded-full border text-[11px] font-mono uppercase tracking-widest disabled:opacity-50 ${
                        archived
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300"
                          : "border-black/10 bg-white text-charcoal-700 hover:border-black/20"}`}>
                {archived ? "Restore lead" : "Archive"}
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] font-mono uppercase tracking-widest text-charcoal-500">
              <span data-testid="lead-times-seen">
                Requests from this org: <span className="text-charcoal-900">{timesSeen}</span>
              </span>
              {lead.ip && (
                <span data-testid="lead-ip">
                  IP: <span className="text-charcoal-900 normal-case tracking-normal">{lead.ip}</span>
                </span>
              )}
              {archived && lead.archived_reason && (
                <span className="text-charcoal-400">
                  Archived · {lead.archived_reason}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-semibold tracking-tighter text-charcoal-900">{lead.score ?? "—"}<span className="text-lg text-charcoal-400">/100</span></p>
            <p className="text-[10.5px] font-mono uppercase tracking-widest text-charcoal-500 mt-1">First report score</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          {/* Left col: report snapshot + issues */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="lead-issues-card">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Key issues at first scan</p>
              <h3 className="mt-1 text-lg font-medium text-charcoal-900 mb-3">What their site looked like when they arrived</h3>
              {(lead.key_issues || []).length === 0 ? (
                <p className="text-sm text-charcoal-500">No red or amber checks on the first report.</p>
              ) : (
                <ul className="divide-y divide-black/5">
                  {lead.key_issues.map((k, i) => (
                    <li key={i} className="py-2.5 flex items-start gap-3">
                      <span className={`w-2 h-2 rounded-full mt-2 ${k.status === "red" ? "bg-red-500" : "bg-amber-500"}`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-charcoal-900">{k.label}</p>
                        {k.detail && <p className="text-xs text-charcoal-500 mt-0.5">{k.detail}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {data.latest_report && (
              <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-6">
                <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Current health (since becoming a customer)</p>
                <div className="mt-3 flex items-center gap-6">
                  <div>
                    <p className="text-3xl font-semibold tracking-tighter text-charcoal-900">
                      {data.latest_report.overall_score}<span className="text-base text-charcoal-400">/100</span>
                    </p>
                    <p className="text-[10.5px] font-mono uppercase tracking-widest text-charcoal-500 mt-1">Latest score</p>
                  </div>
                  <p className="text-xs text-charcoal-500">
                    Last scanned {data.latest_report.generated_at ? new Date(data.latest_report.generated_at).toLocaleString() : "—"}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right col: timeline + notes */}
          <div className="space-y-6">
            <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="lead-timeline">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Timeline</p>
              <h3 className="mt-1 text-lg font-medium text-charcoal-900 mb-2">Every event, in order</h3>
              {timeline.length === 0 ? (
                <p className="text-sm text-charcoal-500">No events yet.</p>
              ) : (
                <div className="divide-y divide-black/5">
                  {timeline.map((ev, i) => <TimelineEvent key={i} ev={ev} />)}
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="lead-notes">
              <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Internal notes</p>
              <h3 className="mt-1 text-lg font-medium text-charcoal-900 mb-3">Only visible to owners</h3>
              <form onSubmit={addNote} className="mb-4">
                <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
                          data-testid="lead-note-input"
                          rows="3" placeholder="Add an internal note…"
                          className="w-full px-3 py-2 rounded-lg border border-black/10 text-sm focus:outline-none focus:border-charcoal-400" />
                <button type="submit" disabled={saving || !noteText.trim()}
                        data-testid="lead-note-save"
                        className="mt-2 px-3 py-1.5 rounded-full bg-charcoal-900 text-white text-xs font-mono uppercase tracking-widest disabled:opacity-50">
                  {saving ? "Saving…" : "Save note"}
                </button>
              </form>
              {(lead.notes || []).length === 0 ? (
                <p className="text-xs text-charcoal-400">No notes yet.</p>
              ) : (
                <ul className="space-y-3">
                  {[...(lead.notes || [])].reverse().map((n, i) => (
                    <li key={i} className="text-sm text-charcoal-800 border-l-2 border-charcoal-200 pl-3">
                      <p>{n.text}</p>
                      <p className="text-[10.5px] font-mono uppercase tracking-widest text-charcoal-400 mt-1">
                        {n.admin_email} · {new Date(n.ts).toLocaleString()}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
